import { AxiosResponse } from "axios";
import React, { useState } from "react";
import { FileRejection } from "react-dropzone/typings/react-dropzone";
import { Message, Progress } from "semantic-ui-react";
import { ExtractFileUploadInstruction, SingleFileApi, uploadFileToCoreApi } from "utilities/fileUploadUtils";
import { type FileTypes } from "utilities/fileutils";
import { FileDropzone } from "./FileDropzone";
import { useElementHeight } from "utilities/hookutils";
import { getAcceptForFileType, getIconTypeForFileType, getHtmlAcceptForFileType, UploadProgress } from "./utils";

interface FileUploadWithValueProps<
  TApi extends SingleFileApi
> {
  maxHeight: number;
  currentValue: JSX.Element | null;
  maxSizeBytes: number;
  fileType: Exclude<FileTypes, "unknown">;
  api: TApi,
  endpoint: string,
  instruction: Omit<ExtractFileUploadInstruction<TApi>, "file">,
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

// TODO: This should work for other file types as well
const FileUploadWithValue = <TApi extends SingleFileApi>(props: FileUploadWithValueProps<TApi>) => {
  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState<UploadProgress>({
    totalSize: 0,
    uploaded: 0,
  });

  const displayDropzone = props.currentValue === null;
  const heightHelper = useElementHeight();

  const onFileUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    onDrop(files);
  }

  const onValueClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  }

  const onDrop = async (files: FileList | File[]) => {
    setError(undefined);

    if (files.length !== 1) {
      return;
    }

    const onUploadProgress = (e: ProgressEvent<EventTarget>) => {
      setProgress({
        totalSize: e.total,
        uploaded: e.loaded
      });
    };

    setIsUploading(true);
    const uploadRes = uploadFileToCoreApi(
      props.api,
      props.endpoint,
      {
        ...props.instruction,
        file: files[0]
      },
      {
        onUploadProgress,
        fileType: props.fileType,
        timeout: 60000, // 1 minute
        overrideMaxFileLength: props.maxSizeBytes
      }
    );

    if (typeof uploadRes === "string") {
      setError(uploadRes);
      setIsUploading(false);
      return;
    } else {
      try {
        const fileUploadRes: AxiosResponse<server.dto.FileUploadResult> = await uploadRes;

        if (fileUploadRes.data.hasErrors) {
          setError(fileUploadRes.data.errors[0].message);
          setIsUploading(false);
        } else {
          props.onUploadComplete(fileUploadRes.data.fileId, fileUploadRes.data.downloadUrl);
          setError(undefined);
          setIsUploading(false);
        }
      } catch {
        setError(RESX.File.ThereWasAProblemUploadingTheFile);
        setIsUploading(false);
      }
    }
  }

  const onDropReject = (fileRejections: FileRejection[]) => {
    const firstRejectionError = fileRejections[0].errors[0].message;
    setError(firstRejectionError);
  }

  return <div style={{
    minHeight: heightHelper.height,
    height: "100%",
  }} ref={heightHelper.ref} className="grow1 flex-column gap-small">
    {(!displayDropzone && !isUploading) && <>
      <input
        ref={fileUploadRef}
        onChange={onFileUploadInputChange}
        type="file"
        accept={getHtmlAcceptForFileType(props.fileType)}
        hidden
      />
      <div onClick={onValueClick} style={{ maxHeight: props.maxHeight, height: "100%" }} className="file-upload-image-wrapper">
        {props.currentValue}
      </div>
    </>}
    {(displayDropzone && !isUploading) && <FileDropzone
      iconType={getIconTypeForFileType(props.fileType)}
      accept={getAcceptForFileType(props.fileType)}
      maxFiles={1}
      maxSizeBytes={props.maxSizeBytes}
      onDrop={onDrop}
      onDropReject={onDropReject}
    />}
    {isUploading && <div style={{ width: "100%" }}>
      <Progress
        percent={progress.uploaded / progress.totalSize * 100}
        color="green"
        progress
        label={RESX.File.UploadingFiles}
        active={true} />
    </div>}
    {error && <div>
      <Message color="red">{error}</Message>
    </div>}
  </div>
}

interface BrandFileUploadProps {
  brandId: number;
  url: string | null;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const BrandLogoFileUpload = (props: BrandFileUploadProps) => {
  return <FileUploadWithValue
    maxHeight={2000}
    fileType="image"
    maxSizeBytes={serverReference.constants.file.maximumImageFileContentLength}
    currentValue={props.url ? <img
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      src={props.url}
    /> : null}
    api={window.CoreApi.Brand.SetLogo}
    endpoint={window.CoreApi.Brand.SetLogo.absoluteUrl()}
    instruction={{
      brandId: props.brandId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

export const BrandIconFileUpload = (props: BrandFileUploadProps) => {
  return <FileUploadWithValue
    maxHeight={2000}
    fileType="image"
    maxSizeBytes={serverReference.constants.file.maximumImageFileContentLength}
    currentValue={props.url ? <img
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      src={props.url}
    /> : null}
    api={window.CoreApi.Brand.SetIcon}
    endpoint={window.CoreApi.Brand.SetIcon.absoluteUrl()}
    instruction={{
      brandId: props.brandId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface BookkeepingLogoFileUploadProps {
  bookkeepingId: number;
  logo: server.dto.BookkeepingLogoFile | null;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const BookkeepingLogoFileUpload = (props: BookkeepingLogoFileUploadProps) => {
  return <FileUploadWithValue
    maxHeight={180}
    fileType="image"
    maxSizeBytes={serverReference.constants.file.maximumImageFileContentLength}
    currentValue={props.logo ? <img
      style={{ maxWidth: "100%", maxHeight: "100%" }}
      src={props.logo.downloadUrl}
    /> : null}
    api={window.CoreApi.Bookkeeping.SetLogo}
    endpoint={window.CoreApi.Bookkeeping.SetLogo.absoluteUrl()}
    instruction={{
      bookkeepingId: props.bookkeepingId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}